/* eslint-disable react/no-unstable-nested-components */
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';

import { ChatChannelIds, ChatMessage as ChatMessageType, ClientType, MAX_CHAT_MESSAGE_LIMIT, evaluateChatLinks } from '@core/types';
import { Tab, Tabs } from '@core/ui';
import { useTranslation } from 'react-i18next';

import {
    chatRejectionHandled,
    onDemandChatRejectionHandled,
    onDemandSendChatMessage,
    sendChatMessage,
    useAppDispatch,
    useAppSelector
} from '@online-webinar/store';
import {
    ChatMessageButton,
    ChatMessageErrorLabel,
    ChatMessageLimitExceedLabel,
    ChatMessageTextInput,
    ChatUserAvatarPic,
    ChatUserOnlineStatus,
    GlobalLabel,
    MessageDeleted,
    MessageText
} from '../../atoms';
import { ChatUserAvatar, MessageBody, MessageContent, MessageInfo, RelatedMessageContent, SendMessageInput } from '../../molecules';
import { UpdateNickname } from '../../molecules/UpdateNickname';
import { ChatMessage, ChatMessages } from '../../organisms';

const NO_LOCAL_MESSAGES: ChatMessageType[] = [];

export const ChatViewer = ({ isOnDemand }: { isOnDemand?: boolean }) => {
    const { t } = useTranslation();
    const [messageToSend, setMessageToSend] = useState('');
    const [maxCharMessageReached, setMaxCharMessageReached] = useState(false);
    const [messages, setMessages] = useState<ChatMessageType[]>([]);
    const [linkError, setLinkError] = useState<'Chat.Message.LinkNotAllowed' | 'Chat.Message.TooManyLinks' | null>(null);
    const dispatch = useAppDispatch();

    const chatChannels = useAppSelector((state) => (isOnDemand ? state.onDemandWebinar : state.liveWebinar).chatChannels);
    const chatSettings = useAppSelector((state) => (isOnDemand ? state.onDemandWebinar : state.liveWebinar).chatSettings);
    const me = useAppSelector((state) => (isOnDemand ? state.onDemandWebinar : state.liveWebinar).me);
    const policy = useAppSelector((state) => state.chatLinkPolicy);
    const localSentMessages = useAppSelector((state) => (isOnDemand ? state.onDemandWebinar.localSentMessages : NO_LOCAL_MESSAGES));
    const rejection = useAppSelector((state) => (isOnDemand ? state.onDemandWebinar : state.liveWebinar).lastChatRejection);

    // A refused send comes back through the store: restore the draft, show why, and consume the rejection
    // so a remount does not replay it.
    useEffect(() => {
        if (!rejection || !me || rejection.channelId !== me.participantId) return;
        setMessageToSend(rejection.message);
        setLinkError(rejection.errorCode === 'ChatLinkNotAllowedException' ? 'Chat.Message.LinkNotAllowed' : null);
        dispatch(isOnDemand ? onDemandChatRejectionHandled() : chatRejectionHandled());
    }, [rejection, me, isOnDemand, dispatch]);

    const onChatInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (MAX_CHAT_MESSAGE_LIMIT - e.target.value.length > 20) {
            setMaxCharMessageReached(false);
        } else if (MAX_CHAT_MESSAGE_LIMIT - e.target.value.length <= 20) {
            setMaxCharMessageReached(true);
        }
        setMessageToSend(e.target.value);
        setLinkError(null);
    };

    const sendMessage = (message: string) => {
        if (!message || !me) {
            return;
        }
        // Fast feedback only — ChatGrain enforces the same rules on every send.
        const verdict = evaluateChatLinks([message], policy);
        if (verdict === 'blocked' || verdict === 'invalid') {
            setLinkError('Chat.Message.LinkNotAllowed');
            return;
        }
        if (verdict === 'tooMany') {
            setLinkError('Chat.Message.TooManyLinks');
            return;
        }
        if (isOnDemand) {
            dispatch(onDemandSendChatMessage({ message, channelId: me!.participantId }));
        } else {
            dispatch(sendChatMessage({ message, channelId: me!.participantId }));
        }
        setMessageToSend('');
        setMaxCharMessageReached(false);
        setLinkError(null);
    };

    const onChatKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Prevents the newline character from being added
            sendMessage(messageToSend);
        }
    };

    useEffect(() => {
        let all: ChatMessageType[] = [];
        if (ChatChannelIds.publicChannel in chatChannels) {
            all = [...all, ...chatChannels[ChatChannelIds.publicChannel].messages];
        }
        if (me!.participantId in chatChannels) {
            all = [...all, ...chatChannels[me!.participantId].messages];
        }
        if (localSentMessages.length > 0) {
            all = [
                ...all,
                ...localSentMessages,
                {
                    timestamp: new Date().toISOString(),
                    messageId: '',
                    channelId: me!.participantId,
                    senderClientType: ClientType.on_demand,
                    senderParticipantId: '',
                    senderNickname: t('Chat.OnDemandChat.Nickname'),
                    message: t('Chat.OnDemandChat.Message'),
                    isDeleted: false,
                    isPermanentlyDeleted: false
                }
            ];
        }
        setMessages(all.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()));
    }, [chatChannels, localSentMessages, me]);

    if (!me) {
        return <></>;
    }

    return (
        <div className="flex flex-col min-h-0 flex-1 w-full bg-gray-100" data-testid="ChatMessagesContent">
            <div className="flex-1 flex flex-col bg-gray-200">
                <Tabs>
                    <Tab
                        classNames="text-sm flex-1"
                        component={
                            <ChatMessages
                                channelId={ChatChannelIds.publicChannel}
                                classNames="flex-1 bg-white"
                                messages={messages}
                                showOnDemandChatInfo={isOnDemand && chatSettings.enableOnDemandChatForwarding}
                                itemContent={(index: number, message: ChatMessageType) => (
                                    <ChatMessage
                                        data-testid={`message${index}`}
                                        key={message.messageId}
                                        ownMessage={me?.participantId === message.senderParticipantId}
                                    >
                                        <ChatUserAvatar>
                                            <ChatUserAvatarPic />
                                            <ChatUserOnlineStatus />
                                        </ChatUserAvatar>
                                        <MessageContent ownMessage={me?.participantId === message.senderParticipantId}>
                                            <MessageInfo username={message.senderNickname} time={message.timestamp} />
                                            <MessageBody>
                                                {!message.isDeleted && message.relatedMessage && (
                                                    <>
                                                        <RelatedMessageContent>
                                                            <MessageInfo
                                                                relationType={message.relationType}
                                                                username={message.relatedMessage.senderNickname}
                                                                time={message.relatedMessage.timestamp}
                                                            />
                                                            <MessageBody>
                                                                {!message.relatedMessage?.isDeleted && (
                                                                    <MessageText messageBody={message.relatedMessage.message} />
                                                                )}
                                                                {message.relatedMessage?.isDeleted && <MessageDeleted />}
                                                            </MessageBody>
                                                        </RelatedMessageContent>
                                                    </>
                                                )}
                                                <GlobalLabel isGlobal={message.channelId === ChatChannelIds.publicChannel} />
                                                {!message.isDeleted && <MessageText messageBody={message.message} />}
                                                {message.isDeleted && <MessageDeleted />}
                                            </MessageBody>
                                        </MessageContent>
                                    </ChatMessage>
                                )}
                            />
                        }
                        active
                    >
                        <span data-testid="ChatHeader">{t('Chat.ChannelName.All')}</span>
                    </Tab>
                </Tabs>
            </div>

            {maxCharMessageReached && (
                <div className="z-20 sticky bottom-0">
                    <ChatMessageLimitExceedLabel messageSize={MAX_CHAT_MESSAGE_LIMIT - messageToSend.length} />
                </div>
            )}
            {linkError && (
                <div className="z-20 sticky bottom-0">
                    <ChatMessageErrorLabel messageKey={linkError} />
                </div>
            )}
            {((!isOnDemand && me?.nickname) || (isOnDemand && chatSettings.enableOnDemandChatForwarding)) && (
                <div className="z-20 bg-accent-lighter">
                    <SendMessageInput>
                        <ChatMessageTextInput
                            value={messageToSend}
                            onChatInputChange={onChatInputChange}
                            onChatKeyDown={onChatKeyDown}
                            placeholder={t('Player.Chat.InputPlaceHolder')}
                            maxLength={MAX_CHAT_MESSAGE_LIMIT}
                        />
                        <ChatMessageButton
                            onClick={() => sendMessage(messageToSend)}
                            disabled={messageToSend.length - MAX_CHAT_MESSAGE_LIMIT === 0 || messageToSend.length === 0}
                        />
                    </SendMessageInput>
                </div>
            )}
            {!isOnDemand && !me?.nickname && <UpdateNickname />}
        </div>
    );
};
